import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * Get the user's browser timezone
 * @returns {string} User's timezone identifier (e.g., 'America/New_York')
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get timezone abbreviation for display (e.g., EST, PST, CET)
 * @param {string} timezone - Timezone identifier (e.g., 'America/New_York')
 * @param {Date} date - Date to get abbreviation for (handles DST)
 * @returns {string} Timezone abbreviation
 */
export function getTimezoneAbbreviation(timezone, date = new Date()) {
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
}

/**
 * Convert UTC timestamp to specified timezone and extract hour
 * @param {string} utcTimestamp - UTC timestamp string
 * @param {string} timezone - Target timezone identifier
 * @returns {number} Hour in target timezone (0-23)
 */
export const getHourInTimezone = (utcTimestamp, timezone) => {
  try {
    const utcDate = new Date(utcTimestamp);
    const zonedTime = toZonedTime(utcDate, timezone);
    return zonedTime.getHours();
  } catch (error) {
    console.error('Error converting timezone:', error);
    // Fallback to UTC hour
    return new Date(utcTimestamp).getUTCHours();
  }
};

/**
 * Format a date in the specified timezone
 * @param {Date|string} date - Date to format
 * @param {string} timezone - Timezone identifier
 * @param {string} formatString - Format string (default: 'yyyy-MM-dd HH:mm:ss')
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, timezone, formatString = 'yyyy-MM-dd HH:mm:ss') => {
  try {
    return formatInTimeZone(date, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    // Fallback to regular formatting
    return format(new Date(date), formatString);
  }
};

/**
 * Get list of common timezones for dropdown selection
 * @returns {Array} Array of timezone objects with label and value
 */
export function getCommonTimezones() {
  let userTz;
  let userTzAbbrev;
  
  try {
    userTz = getUserTimezone();
    userTzAbbrev = getTimezoneAbbreviation(userTz);
  } catch (error) {
    console.warn('Error getting user timezone in getCommonTimezones, using UTC as fallback:', error);
    userTz = 'UTC';
    userTzAbbrev = 'UTC';
  }
  
  const commonTimezones = [
    { label: `Your Timezone (${userTzAbbrev})`, value: userTz },
    { label: 'UTC', value: 'UTC' },
    { label: 'EST/EDT (New York)', value: 'America/New_York' },
    { label: 'CST/CDT (Chicago)', value: 'America/Chicago' },
    { label: 'MST/MDT (Denver)', value: 'America/Denver' },
    { label: 'PST/PDT (Los Angeles)', value: 'America/Los_Angeles' },
    { label: 'CET/CEST (Paris)', value: 'Europe/Paris' },
    { label: 'GMT (London)', value: 'Europe/London' },
    { label: 'JST (Tokyo)', value: 'Asia/Tokyo' },
    { label: 'AEST/AEDT (Sydney)', value: 'Australia/Sydney' },
    { label: 'IST (India)', value: 'Asia/Kolkata' },
    { label: 'CST (China)', value: 'Asia/Shanghai' },
  ];
  
  // Remove duplicate if user timezone is already in common list
  const userTzInList = commonTimezones.some(tz => tz.value === userTz && tz.label !== `Your Timezone (${userTzAbbrev})`);
  if (userTzInList) {
    return commonTimezones.slice(1); // Remove the "Your Timezone" entry
  }
  
  return commonTimezones;
}

/**
 * Re-bucket hourly data from UTC to target timezone
 * @param {Array} reportData - Array of device data with hourlyData
 * @param {string} targetTimezone - Target timezone identifier
 * @returns {Array} Hourly data array (0-23) with timezone-adjusted aggregation
 */
export const rebucketHourlyDataByTimezone = (reportData, targetTimezone) => {
  if (!reportData || reportData.length === 0) {
    return Array.from({ length: 24 }, (_, hour) => ({
      hour,
      total: 0,
      count: 0,
      deviceCount: 0,
      medianUsage: 0
    }));
  }
  
  // Initialize timezone-adjusted hourly buckets
  const timezoneHourlyData = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    total: 0,
    count: 0,
    deviceUsages: [] // For median calculation
  }));
  
  // Process each device's hourly data
  reportData.forEach(device => {
    if (!device.hourlyData) return;
    
    // For each UTC hour bucket in the device data
    device.hourlyData.forEach((hourData, utcHour) => {
      if (!hourData || hourData.total === 0) return;
      
      // Create a representative UTC timestamp for this hour
      // Use today's date with the UTC hour to get timezone conversion
      const today = new Date();
      const utcTimestamp = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), utcHour, 0, 0, 0)
      );
      
      // Convert to target timezone hour
      const targetHour = getHourInTimezone(utcTimestamp.toISOString(), targetTimezone);
      
      // Add this device's data to the appropriate timezone hour bucket
      timezoneHourlyData[targetHour].total += hourData.total;
      timezoneHourlyData[targetHour].count += hourData.count;
      
      // Store individual device consumption for median calculation  
      // hourData.total is now actual consumption per device for this hour
      if (hourData.total > 0) {
        timezoneHourlyData[targetHour].deviceUsages.push(hourData.total);
      }
    });
  });
  
  // Calculate final aggregated data and median for each hour
  return timezoneHourlyData.map(hourData => {
    const deviceUsages = hourData.deviceUsages.filter(usage => usage > 0);
    const medianUsage = deviceUsages.length > 0 
      ? deviceUsages.sort((a, b) => a - b)[Math.floor(deviceUsages.length / 2)]
      : 0;
      
    return {
      hour: hourData.hour,
      total: hourData.total,
      count: hourData.count,
      deviceCount: deviceUsages.length,
      medianUsage: medianUsage
    };
  });
};

/**
 * Format hour label with timezone abbreviation
 * @param {number} hour - Hour (0-23)
 * @param {string} timezone - Timezone identifier
 * @returns {string} Formatted hour label (e.g., "14:00 EST")
 */
export const formatHourWithTimezone = (hour, timezone) => {
  const hourStr = hour.toString().padStart(2, '0') + ':00';
  const tzAbbrev = getTimezoneAbbreviation(timezone);
  return `${hourStr} ${tzAbbrev}`;
};